import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeNotifications, markNotificationRead } from '../services/firebase/notificationsService';
import type { AppNotification } from '../types/notifications';
import { initNotificationSound, playNotificationSound } from './notificationSound';

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  muted: boolean;
  setMuted: (v: boolean) => void;
  markRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  muted: false,
  setMuted: () => undefined,
  markRead: async () => undefined,
});

export const useNotifications = () => useContext(NotificationContext);

const MUTE_KEY = 'jk_notification_mute';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [muted, setMutedState] = useState(() => localStorage.getItem(MUTE_KEY) === 'true');
  const seenRef = useRef<Set<string>>(new Set());

  const setMuted = (v: boolean) => {
    setMutedState(v);
    localStorage.setItem(MUTE_KEY, String(v));
  };

  useEffect(() => {
    initNotificationSound();
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      
return;
    }
    
return subscribeNotifications(user.uid, (list) => {
      list.forEach((n) => {
        if (!n.isRead && !seenRef.current.has(n.id)) {
          seenRef.current.add(n.id);
          playNotificationSound(muted);
        }
      });
      setNotifications(list);
    });
  }, [user?.uid, muted]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications],
  );

  const markRead = async (id: string) => {
    if (!user?.uid) return;
    await markNotificationRead(user.uid, id);
  };

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, muted, setMuted, markRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
